// TEMP: this would broken away into multiple generic wrapper components

import { Theme, Grid, Typography } from '@mui/material';
import ArrowIcon from '@mui/icons-material/ArrowForward';
import { Difference } from 'store/request/differences/types';
import { makeStyles } from 'tss-react/mui';
import { DifferenceTypeNames } from 'types';
import { useSelector } from 'react-redux';
import { getIsSingleFile } from 'store';
import cx from 'classnames';

const DESCRIPTION_WIDTHS = {
  NORMAL: '126px',
  COMPACT: '100px',
  EXTRA_NORMAL: '100%',
  EXTRA_COMPACT: '90%',
  FONT_METRICS_MIN: '250px',
} as const;

const CALC_WIDTHS = {
  NORMAL: 'calc(50% - 20px)',
  COMPACT: 'calc(50% - 40px)',
} as const;

export const useStyles = makeStyles()((theme: Theme) => {
  return {
    description: {
      width: DESCRIPTION_WIDTHS.NORMAL,
      minWidth: DESCRIPTION_WIDTHS.NORMAL,
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    arrow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      width: theme.spacing(6),
      minWidth: theme.spacing(6),
    },
    arrowThumbnail: {
      marginLeft: '-4px',
    },
    arrowSpace: {
      width: theme.spacing(6),
      minWidth: theme.spacing(6),
    },
    flexFixed: {
      flex: '0 0 auto',
    },
    pageDescription: {
      fontSize: '10px',
      color: 'var(--gds-color-text-supportive)',
      marginTop: theme.spacing(1),
      letterSpacing: 1,
      fontWeight: 700,
    },
    differencePageNumber: {
      textTransform: 'uppercase',
      fontSize: '9px',
    },
    icons: {
      maxWidth: theme.spacing(2),
      maxHeight: theme.spacing(2),
      margin: '2px',
    },
    iconsExtra: {
      position: 'relative',
      top: theme.spacing(0.6),
    },
    extra: {
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      width: '100%',
    },
    extraTextLayout: {
      whiteSpace: 'pre-line',
      overflow: 'visible',
      wordBreak: 'keep-all',
      overflowWrap: 'break-word',
    },
    fontMetricsSource: {
      display: 'flex',
      fontSize: '12px',
      width: '100%',
      minWidth: DESCRIPTION_WIDTHS.FONT_METRICS_MIN,
      whiteSpace: 'nowrap',
    },
    fontMetricsExtra: {
      width: '100%',
      maxWidth: '100%',
      minWidth: 'auto',
      fontWeight: 600,
      color: '#8892AB',
      fontSize: '11px',
      marginTop: theme.spacing(0.5),
      letterSpacing: '0px',
    },
    descriptionContent: {
      minWidth: 0,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'hidden',
      overflowWrap: 'break-word',
    },
    fixedNormal: {
      width: DESCRIPTION_WIDTHS.NORMAL,
      minWidth: DESCRIPTION_WIDTHS.NORMAL,
    },
    fixedCompact: {
      width: DESCRIPTION_WIDTHS.COMPACT,
      minWidth: DESCRIPTION_WIDTHS.COMPACT,
    },
    fluidNormal: {
      flexBasis: CALC_WIDTHS.NORMAL,
    },
    fluidCompact: {
      flexBasis: CALC_WIDTHS.COMPACT,
    },
    sourceEllipsis: {
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    centeredContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    centeredText: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    pageNumberColumnFixed: {
      flex: '1 1 0%',
      transition: 'width 300ms ease-out, min-width 300ms ease-out',
    },
    pageNumberColumnFluid: {
      flex: '1 1 0%',
      minWidth: 0,
      transition: 'flex-basis 300ms ease-out',
    },
    // Extra section width variants
    extraWidthNormal: {
      width: DESCRIPTION_WIDTHS.EXTRA_NORMAL,
    },
    extraWidthCompact: {
      width: DESCRIPTION_WIDTHS.EXTRA_COMPACT,
    },
  };
});

export interface DifferenceDescriptionProps {
  difference: Difference;
  sourceDescription?: React.ReactNode;
  targetDescription?: React.ReactNode;
  sourcePageNumberText?: string;
  targetPageNumberText?: string;
  showExtra?: boolean;
  isBold?: boolean;
  renderIcon?: () => React.ReactNode;
  extraDescription?: string;
  multipleSelectionMode?: boolean;
  /** ResultCard thumbnail row. Replaces the source/target columns and page labels. */
  media?: React.ReactNode;
}

const getExtraToDisplay = (extra = ''): string => {
  const extraToDisplay = extra.replace(new RegExp('^:'), '');
  return extraToDisplay.trim();
};

const DifferenceDescription = ({
  difference,
  isBold = false,

  // prop changed in insertion
  sourceDescription = (
    <Typography sx={{ fontWeight: isBold ? 800 : 600 }}>{difference.source.text?.substring(0, 100)}</Typography>
  ),

  // prop changed in spelling and deletion
  targetDescription = (
    <Typography sx={{ fontWeight: isBold ? 800 : 600 }}>{difference.target.text?.substring(0, 100)}</Typography>
  ),

  // prop changed in spelling and Insertion
  sourcePageNumberText = `source (p${difference.source.location.pageNumber})`,

  // prop changed in spelling and deletion
  targetPageNumberText = `new (p${difference.target.location.pageNumber})`,

  // prop disabled in spelling
  showExtra = Boolean(difference.description || difference.fontMetricsDetails),
  extraDescription,
  renderIcon,
  multipleSelectionMode = false,
  media,
}: DifferenceDescriptionProps) => {
  const { classes } = useStyles();
  const pageNumberClass = `${classes.pageDescription} ${classes.differencePageNumber}`;
  const isProofreaderMode = useSelector(getIsSingleFile);
  const isUnmatchedAnnotation = difference.type === DifferenceTypeNames.UnmatchedAnnotation;
  const isMarkupDifference = difference.type === DifferenceTypeNames.Markup;
  const isFontMetrics = difference.type === DifferenceTypeNames.FontMetrics;
  const isFontDifference =
    difference.type === DifferenceTypeNames.FontType ||
    difference.type === DifferenceTypeNames.FontSize ||
    difference.type === DifferenceTypeNames.FontColor ||
    difference.type === DifferenceTypeNames.FontMetrics;
  const isProofreaderMarkup = isMarkupDifference && isProofreaderMode;

  if (isFontDifference) {
    const displayName = difference.displayName.split(' ');
    const description = `${
      displayName[0].charAt(0).toUpperCase() + displayName[0].slice(1)
    } ${displayName[1].toLowerCase()} difference${difference.groupId ? 's' : ''}`;
    sourceDescription = (
      <Typography sx={{ fontWeight: isBold ? 800 : 600 }} variant="subtitle1">
        {description}
      </Typography>
    );
  }
  if (isUnmatchedAnnotation) {
    sourceDescription = (
      <Typography sx={{ fontWeight: isBold ? 800 : 600 }}>
        {difference.annotationDetail?.comments[0].content.substring(0, 100)}
      </Typography>
    );
  }
  if (isFontMetrics) {
    const fontSizeCheckValue = difference.fontMetricsDetails.fontSizeCheckValue;
    const fontPointSize = difference.fontMetricsDetails.fontPointSize;
    const fontSizeStatus = difference.fontMetricsDetails.contextTag;
    const globalFontSizeStatus = difference.fontMetricsDetails.isGlobalFontResult;
    let sourceText = '';

    if (fontSizeStatus.toLowerCase() === 'pass') {
      sourceText = `Minimum ${fontSizeCheckValue || ''}pt confirmed`;
    } else if (fontSizeStatus.toLowerCase() === 'fail' && globalFontSizeStatus) {
      sourceText = `Minimum ${fontSizeCheckValue || ''}pt failed`;
    } else {
      sourceText = `Font size found: ${fontPointSize || ''}pt`;
    }
    const calculateExtraDescription = () => {
      if (fontSizeStatus.toLowerCase() === 'pass') {
        return `The document passes the minimum ${fontSizeCheckValue || ''}pt requirement.`;
      } else if (globalFontSizeStatus) {
        return `The document fails the minimum ${fontSizeCheckValue || ''}pt requirement.`;
      } else {
        return `This content fails the minimum ${fontSizeCheckValue || ''}pt requirement.`;
      }
    };
    sourceDescription = (
      <Typography sx={{ fontWeight: isBold ? 800 : 600 }} className={classes.fontMetricsSource}>
        {sourceText}
      </Typography>
    );
    extraDescription = calculateExtraDescription();
  }

  const differenceHasThumbnailOrPlaceholder =
    difference.type === DifferenceTypeNames.Graphics ||
    difference.type === DifferenceTypeNames.EmbeddedImage ||
    difference.type === DifferenceTypeNames.UnmatchedAnnotation ||
    difference.type === DifferenceTypeNames.Markup;

  const getColumnClasses = (
    hasThumbnailOrPlaceholder: boolean,
    multipleSelectionMode: boolean,
    isFontDifference: boolean,
    isSourceColumn: boolean,
  ) =>
    cx(
      hasThumbnailOrPlaceholder
        ? [
            classes.description,
            classes.pageNumberColumnFixed,
            multipleSelectionMode ? classes.fixedCompact : classes.fixedNormal,
          ]
        : [
            classes.descriptionContent,
            classes.pageNumberColumnFluid,
            multipleSelectionMode ? classes.fluidCompact : classes.fluidNormal,
            isFontDifference && isSourceColumn ? classes.sourceEllipsis : null,
          ],
    );

  const extra = showExtra ? (
    <Grid item className={multipleSelectionMode ? classes.extraWidthCompact : classes.extraWidthNormal}>
      <Typography
        data-testid="extra"
        className={cx(
          isFontMetrics ? classes.fontMetricsExtra : `${classes.pageDescription} ${classes.extra}`,
          classes.extraTextLayout,
        )}
      >
        {extraDescription || getExtraToDisplay(difference.description)}
      </Typography>
    </Grid>
  ) : null;

  if (media) {
    return (
      <Grid container item direction="column">
        {media}
        {extra}
      </Grid>
    );
  }

  return (
    <Grid container item direction="column">
      <Grid item container wrap="nowrap" alignItems={'center'}>
        {/* Special case: Center single thumbnail in proofreader mode */}
        {isProofreaderMarkup ? (
          <Grid item className={classes.centeredContainer}>
            {sourceDescription}
          </Grid>
        ) : (
          <>
            <Grid
              item
              className={getColumnClasses(
                differenceHasThumbnailOrPlaceholder,
                multipleSelectionMode,
                isFontDifference,
                true,
              )}
            >
              {sourceDescription}
            </Grid>
            {isFontDifference ? (
              <Grid item className={cx(classes.arrowSpace, classes.flexFixed)} />
            ) : renderIcon ? (
              <Grid
                item
                className={cx(
                  classes.arrow,
                  classes.flexFixed,
                  differenceHasThumbnailOrPlaceholder && !multipleSelectionMode && classes.arrowThumbnail,
                )}
              >
                {renderIcon()}
              </Grid>
            ) : (
              !isFontMetrics &&
              targetDescription != null && (
                <Grid
                  item
                  className={cx(
                    classes.arrow,
                    classes.flexFixed,
                    differenceHasThumbnailOrPlaceholder && !multipleSelectionMode && classes.arrowThumbnail,
                  )}
                >
                  <ArrowIcon className={classes.icons} />
                </Grid>
              )
            )}
            {isFontDifference ? null : (
              <Grid
                item
                className={getColumnClasses(differenceHasThumbnailOrPlaceholder, multipleSelectionMode, false, false)}
              >
                {!isFontDifference && targetDescription}
              </Grid>
            )}
          </>
        )}
      </Grid>
      {!isFontDifference && (sourcePageNumberText || targetPageNumberText) && (
        <Grid item container wrap="nowrap">
          {/* Special case: Center page number in proofreader mode */}
          {isProofreaderMarkup ? (
            <Grid item className={classes.centeredText}>
              <Typography className={pageNumberClass}>{sourcePageNumberText || targetPageNumberText}</Typography>
            </Grid>
          ) : (
            <>
              <Grid
                item
                className={getColumnClasses(differenceHasThumbnailOrPlaceholder, multipleSelectionMode, false, false)}
              >
                <Typography className={pageNumberClass}>{sourcePageNumberText}</Typography>
              </Grid>
              <Grid
                item
                className={cx(
                  classes.arrowSpace,
                  classes.flexFixed,
                  differenceHasThumbnailOrPlaceholder && !multipleSelectionMode && classes.arrowThumbnail,
                )}
              />
              <Grid
                item
                className={getColumnClasses(differenceHasThumbnailOrPlaceholder, multipleSelectionMode, false, false)}
              >
                <Typography className={pageNumberClass}>{targetPageNumberText}</Typography>
              </Grid>
            </>
          )}
        </Grid>
      )}
      {extra}
    </Grid>
  );
};

export default DifferenceDescription;
